import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import AdminLayout from './layouts/AdminLayout';

// Pages
import Dashboard from './pages/dashboard/Dashboard';
import UsersPage from './pages/users/UsersPage';
import LoginPage from './pages/auth/LoginPage';
import FarmsPage from './pages/farms/FarmsPage';
import FarmDetailPage from './pages/farms/FarmDetailPage';
import PlotsPage from './pages/plots/PlotsPage';
import PlotDetailPage from './pages/plots/PlotDetailPage';
import SubmissionsPage from './pages/submissions/SubmissionsPage';
import BedsPage from './pages/beds/BedsPage';
import BedDetailPage from './pages/beds/BedDetailPage';
import PlantsPage from './pages/plants/PlantsPage';
import PlantDetailPage from './pages/plants/PlantDetailPage';
import SettingsPage from './pages/settings/SettingsPage';
import TikTokCallbackPage from './pages/settings/TikTokCallbackPage';
import TikTokUserShareCallbackPage from './pages/settings/TikTokUserShareCallbackPage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';

/**
 * Protected Route Component
 * 
 * Wrapper component that checks authentication status
 * and redirects to login if not authenticated.
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-secondary-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

/**
 * Role-Based Route Component
 * 
 * Wrapper component that checks user role permissions
 * and redirects to dashboard if not authorized.
 */
const RoleProtectedRoute = ({ children, allowedRoles }) => {
  const { user } = useAuth();

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/**
 * App Routes Component
 * 
 * Main routing logic for the admin panel.
 */
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/bed/:bedId" element={<BedDetailPage />} />
      <Route path="/farm/:farmId" element={<FarmDetailPage />} />
      <Route path="/plot/:plotId" element={<PlotDetailPage />} />
      <Route path="/plant/:plantId" element={<PlantDetailPage />} />
      <Route path="/settings/tiktok-callback" element={<TikTokCallbackPage />} />
      <Route path="/settings/tiktok-share-callback" element={<TikTokUserShareCallbackPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="farms" element={<FarmsPage />} />
        <Route path="plots" element={<PlotsPage />} />
        <Route path="submissions" element={<SubmissionsPage />} />
        <Route path="beds" element={
          <RoleProtectedRoute allowedRoles={['admin', 'super_admin', 'farms_owner']}>
            <BedsPage />
          </RoleProtectedRoute>
        } />
        <Route path="plants" element={
          <RoleProtectedRoute allowedRoles={['admin', 'super_admin', 'farms_owner']}>
            <PlantsPage />
          </RoleProtectedRoute>
        } />
        <Route path="users" element={
          <RoleProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <UsersPage />
          </RoleProtectedRoute>
        } />
        <Route path="settings" element={
          <RoleProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <SettingsPage />
          </RoleProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Route>
    </Routes>
  );
};

/**
 * Main App Component
 * 
 * Root component that sets up providers and routing.
 */
function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#fff',
                color: '#1f2937',
                border: '1px solid #e5e7eb',
              },
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;